import { setCurrentEventContext } from "@features/access/eventContextService";
import { persistPendingEventId } from "@features/access/pendingEventStorage";
import { useAuthStore } from "@features/authentication/stores/authStore";
import { usePhoneAuthStore } from "@features/authentication/stores/phoneAuthStore";
import logger from "@logger";
import useCurrentUser from "@shared/queries/useCurrentUser";
import QUERY_KEYS from "@shared/queryKeys";
import { getReservedPathRedirect, isValidEventSlug } from "@shared/utils/eventIdValidation";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { EventEntryContent } from "./EventEntryContent";
import { hasEventContext } from "./eventEntryUtils";
import { useApplyFirstEventDeepLink } from "./useApplyFirstEventDeepLink";

export default function EventEntryPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { userId, status } = useAuthStore();
  const { user, isLoading, error: userError } = useCurrentUser();
  const { setPendingEventId } = usePhoneAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isApplyingSwitch, setIsApplyingSwitch] = useState(false);
  const [isApplyingFirstEvent, setIsApplyingFirstEvent] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const reservedRedirect = eventId ? getReservedPathRedirect(eventId) : null;
  const validEventId = eventId && isValidEventSlug(eventId) ? eventId : null;

  useEffect(() => {
    if (!validEventId) {
      return;
    }
    setApplyError(null);
    setIsApplyingFirstEvent(false);
    const persisted = persistPendingEventId(validEventId);
    if (persisted) {
      setPendingEventId(persisted);
    }
  }, [validEventId, setPendingEventId]);

  const sessionReady = status !== "pending";
  const profileReady = status !== "authenticated" || (!isLoading && Boolean(userId));
  const onSameEvent = Boolean(validEventId && user && user.lastKnownEventId === validEventId);
  const needsSwitch = Boolean(
    validEventId && user && hasEventContext(user.lastKnownEventId) && user.lastKnownEventId !== validEventId
  );
  // Auto-apply for a first-time join and for re-scans of the current event
  // (the latter refreshes lastKnownEventEnteredAt so presence badges/order update).
  const needsFirstApply = Boolean(validEventId && user && (!hasEventContext(user.lastKnownEventId) || onSameEvent));

  const handleApplyError = useCallback((message: string) => {
    setApplyError(message || null);
  }, []);

  const { retryApply } = useApplyFirstEventDeepLink({
    needsFirstApply,
    validEventId,
    onApplyError: handleApplyError,
    onApplyingChange: setIsApplyingFirstEvent,
  });

  const handleCancelSwitch = () => {
    navigate("/discover", { replace: true });
  };

  const handleConfirmSwitch = async () => {
    if (!validEventId) {
      return;
    }
    setIsApplyingSwitch(true);
    setApplyError(null);
    try {
      await setCurrentEventContext(validEventId);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CURRENT_USER] });
      navigate("/discover", { replace: true });
    } catch (error) {
      logger.error("Failed to switch event context:", error);
      const message = error instanceof Error ? error.message : "Could not switch event. Try again.";
      setApplyError(message);
      setIsApplyingSwitch(false);
    }
  };

  if (!validEventId) {
    return <Navigate replace to={reservedRedirect ?? "/"} />;
  }

  return (
    <EventEntryContent
      reservedRedirect={reservedRedirect}
      validEventId={validEventId}
      sessionReady={sessionReady}
      profileReady={profileReady}
      status={status}
      user={user}
      userErrorMessage={userError?.message ?? null}
      needsSwitch={needsSwitch}
      needsFirstApply={needsFirstApply}
      applyError={applyError}
      isApplyingFirstEvent={isApplyingFirstEvent}
      isApplyingSwitch={isApplyingSwitch}
      onRetryApply={retryApply}
      onCancelSwitch={handleCancelSwitch}
      onConfirmSwitch={() => void handleConfirmSwitch()}
    />
  );
}
